import { Button, FileDropZone, useToastContext } from '@payaca/ambrosia';
import {
  CustomFieldHelperType,
  UploadCustomFieldDefinition,
} from '@payaca/custom-fields';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useState } from 'react';

// todo: use FieldProps<'upload'>
export type UploadFieldInputProps = {
  definition: UploadCustomFieldDefinition;
  value?: CustomFieldHelperType['upload']['value'] | null;
  onChange: (value?: CustomFieldHelperType['upload']['value'] | null) => void;
  handlePersistUpload: (files: File[]) => Promise<
    {
      url: string;
      mimeType: string;
      thumbnailUrl?: string;
      fileName: string;
    }[]
  >;
};

export const UploadFieldInput: FC<UploadFieldInputProps> = (props) => {
  const { definition, value, handlePersistUpload, onChange } = props;

  if (!handlePersistUpload) {
    console.error(
      'handlePersistUpload function is required for upload fields.'
    );
    return null;
  }

  return (
    <MultiUploadInput
      definition={definition}
      value={value ?? undefined}
      handlePersistUpload={handlePersistUpload}
      onChange={onChange}
    />
  );
};

type MultiUploadInputProps = {
  definition: UploadCustomFieldDefinition;
  value?: CustomFieldHelperType['upload']['value'];
  handlePersistUpload: UploadFieldInputProps['handlePersistUpload'];
  onChange: (value?: CustomFieldHelperType['upload']['value'] | null) => void;
};

const MultiUploadInput: FC<MultiUploadInputProps> = (props) => {
  const { definition, value = [], handlePersistUpload, onChange } = props;
  const { pushError } = useToastContext();

  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleDrop = (files: File[]) => {
    const currentFileCount = value?.length || 0;
    const maxFiles = definition.limit;
    const availableSlots = maxFiles - currentFileCount;

    if (availableSlots <= 0) {
      pushError(
        `You can only upload ${maxFiles} image${maxFiles > 1 ? 's' : ''}.`
      );
      return;
    }

    const filesToUpload = files.slice(0, availableSlots);

    setIsLoading(true);
    setHasError(false);

    handlePersistUpload(filesToUpload)
      .then((uploadedFiles) => {
        const newFiles = uploadedFiles.map((file) => ({
          url: file.url,
          mimeType: file.mimeType,
          thumbnailUrl: file.thumbnailUrl,
          name: file.fileName,
        }));

        onChange([...(value || []), ...newFiles]);
      })
      .catch((error) => {
        console.error('Error uploading files:', error);
        pushError('Error uploading files. Please try again.');
        setHasError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteFile = (index: number) => {
    const newValue =
      value?.filter((_: unknown, i: number) => i !== index) || [];
    onChange(newValue.length > 0 ? newValue : null);
  };

  const remainingSlots = definition.limit - (value?.length || 0);

  return (
    <div className="space-y-4">
      {definition.type === 'upload' &&
        Array.isArray(value) &&
        value?.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-3">
            {value?.map((file, index) => (
              <div key={index} className="relative size-20">
                <img
                  src={file.thumbnailUrl}
                  alt={file.name}
                  className="h-full w-full rounded-md object-cover"
                />

                <Button
                  type="button"
                  variant={'white'}
                  onClick={() => {
                    handleDeleteFile(index);
                  }}
                  className="size-6 text-xs absolute top-2 right-2 p-1 rounded-full border cursor-pointer"
                >
                  <UntitledIcon name={'x-close'} className="w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

      {remainingSlots > 0 && (
        <>
          <FileDropZone
            isLoading={isLoading}
            hasError={hasError}
            dropzoneOptions={{
              onDrop: handleDrop,
              accept: definition.acceptedFileExtensions,
              multiple: remainingSlots > 1,
              maxFiles: remainingSlots,
            }}
            remainingSlots={remainingSlots}
          />

          <p className="mt-2">Maximum uploads: {definition.limit}</p>
        </>
      )}
    </div>
  );
};
